import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../../reducers";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";
import { ICallAPIFunction } from "../../../yoello-lib/modules/API2/types";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import { ICollectionPoint } from "../../ordersSetting/Forms/CollectionPointEntity";
import { useOrders } from "../../util/OrderContext";
import { IProcessedOrders } from "../constants";

export function useAllocateOrders(collectionPoints: ICollectionPoint[], isDisabled: boolean) {
    const { callAPI } = useAPIV2(APIExtensions.placedOrders, { onMountDisabled: true, query: "collection_point/acquire" });
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const { newOrders, openedOrders, setMessageOptions } = useOrders()!;
    const isAllocating = useRef(false);

    const cPs = useMemo(() => {
        if (!collectionPoints) return [];
        return collectionPoints.filter((cp) => cp.enabled);
    }, [collectionPoints]);

    useEffect(() => {
        if (isDisabled || !Array.isArray(newOrders) || !Array.isArray(openedOrders)) return;
        console.log("order allocating");
        if (isAllocating.current) {
            console.log("Orders already allocating");
            return;
        };
        isAllocating.current = true;
        const orderList = [...newOrders, ...openedOrders].filter((orders) => orders.order_type === "COLLECTION_POINT_PICKUP" && orders.order_status !== "READY_FOR_COLLECTION") as IProcessedOrders[];

        allocateOrders(cPs, orderList, callAPI, selectedVenueId).then((refreshOrders) => {
            if (refreshOrders) setMessageOptions(refreshOrders);
            isAllocating.current = false;
        });
        //eslint-disable-next-line
    }, [openedOrders?.length, newOrders?.length, isDisabled, cPs]);


}

async function allocateOrders(collectionPoints: ICollectionPoint[], orderList: IProcessedOrders[], callAPI: ICallAPIFunction<any>, venueId: string) {
    const workload = [];
    const { unallocatedOrders, allocatedOrders } = splitOrders(orderList);
    let totalCalls = 0;
    const totalUnallocated = unallocatedOrders.length;
    if (totalUnallocated > 0) {
        for (const cp of collectionPoints) {
            /**Total calls so far greater than total unallocated order break */
            if (totalCalls >= totalUnallocated) break;
            if (!cp.enabled || cp.deleted) break;

            const orderLength = allocatedOrders[cp.id!]?.length ?? 0;

            if (orderLength < cp.max_number_of_concurrent_orders) {
                const amount = cp.max_number_of_concurrent_orders - orderLength;
                let totalCount = totalUnallocated % collectionPoints.length;
                if (totalCount > amount) {
                    totalCount = amount;
                }
                totalCalls += totalCount;
                const body = {
                    venue_id: venueId,
                    collection_point_id: cp.id!,
                    count: totalCount
                }
                workload.push(callAPI(HTTPMethods.POST, { body }));
            }
        }
    }
    await Promise.all(workload);
    return workload.length > 0;
}

/**Split orders into unallocated list and object of allocated by collection point id */
function splitOrders(orderList: IProcessedOrders[]) {
    const unallocatedOrders: IProcessedOrders[] = [];
    const allocatedOrders: Record<string, IProcessedOrders[]> = {};
    for (const item of orderList) {
        if (item.collection_point_id === null) {
            unallocatedOrders.push(item);
        } else {
            const collectionObj = allocatedOrders[item.collection_point_id];
            if (collectionObj) {
                collectionObj.push(item);
            } else {
                allocatedOrders[item.collection_point_id] = [item];
            }
        }
    }
    return {
        unallocatedOrders,
        allocatedOrders
    }
}